// Code Injection Preset Configurations
// Pre-configured combinations of components for different use cases

import { CodeInjectionPreset, DEFAULT_GLOBAL_SETTINGS } from '@/types/codeInjection';

// Minimal presets - Simple, clean enhancements
export const minimalPresets: CodeInjectionPreset[] = [
  {
    id: 'clean-simple',
    name: 'Clean & Simple',
    description: 'Minimal enhancements with subtle hover effects',
    icon: '🧹',
    category: 'minimal',
    restaurantTypes: ['cafe', 'bakery', 'healthy'],
    selectedComponents: {
      menuItemMovement: 'menu-item-lift',
      categoryHeaderStyle: 'category-underline',
      menuItemGlow: 'menu-item-subtle-shadow',
      menuItemTextEffects: ['menu-item-title-style'],
      categoryHeaderTextEffects: ['category-base-styling'],
      buttonHoverEffect: 'button-hover-lift',
      serviceChoiceStyles: 'service-enhanced',
      footerEnhancements: ['footer-line']
    },
    globalSettings: {
      ...DEFAULT_GLOBAL_SETTINGS,
      animationSpeed: 'normal'
    }
  },
  {
    id: 'modern-minimal',
    name: 'Modern Minimal',
    description: 'Contemporary design with clean lines',
    icon: '⚡',
    category: 'minimal',
    restaurantTypes: ['modern', 'fusion', 'contemporary'],
    selectedComponents: {
      menuItemMovement: 'menu-item-lift',
      categoryHeaderStyle: 'category-underline',
      menuItemGlow: 'menu-item-brand-glow',
      menuItemTextEffects: ['menu-item-title-color'],
      menuItemImageEffects: [{ id: 'menu-item-image-gradient', subTemplate: 'menu-item-image-gradient-shadow' }],
      categoryHeaderTextEffects: ['category-base-styling'],
      buttonHoverEffect: 'button-hover-lift',
      serviceChoiceStyles: 'service-enhanced',
      footerEnhancements: ['footer-line']
    },
    globalSettings: {
      ...DEFAULT_GLOBAL_SETTINGS,
      animationSpeed: 'normal'
    }
  }
];

// Balanced presets - Good mix of features without overwhelming
export const balancedPresets: CodeInjectionPreset[] = [
  {
    id: 'professional-branded',
    name: 'Professional Branded',
    description: 'Balanced enhancements with brand focus',
    icon: '🏢',
    category: 'balanced',
    restaurantTypes: ['corporate', 'franchise', 'chain'],
    selectedComponents: {
      menuItemMovement: 'menu-item-lift',
      categoryHeaderStyle: 'category-gradient-underline',
      menuItemGlow: 'menu-item-brand-glow',
      menuItemTextEffects: ['menu-item-title-color', 'menu-item-description-fade'],
      menuItemImageEffects: [{ id: 'menu-item-image-gradient', subTemplate: 'menu-item-image-gradient-primary' }],
      categoryHeaderTextEffects: ['category-base-styling'],
      buttonHoverEffect: 'button-hover-lift-and-color',
      buttonPulseEffect: 'button-pulse-glow',
      navBarTextEffects: ['nav-logo-text-glow', 'nav-logo-image-hover-scale'],
      priceTagEnhancements: 'price-tag-single-color',
      serviceChoiceStyles: 'service-enhanced',
      footerEnhancements: ['footer-line']
    },
    globalSettings: {
      ...DEFAULT_GLOBAL_SETTINGS,
      animationSpeed: 'normal',
      // Custom description fade strength
      descriptionFadeStrength: 0.5, // 50% fade strength
      // Custom button shadow hover to use primary color instead of secondary
      buttonShadowHover: {
        type: 'shadow',
        color: 'USE_PRIMARY_COLOR', // Special marker for primary color
        strength: 0.4
      },
      // Enhanced font settings for Dynamic Dish Title Enhancements - both states
      menuItemTextRest: {
        color: '#ffffff',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      },
      menuItemTextHover: {
        color: '#c5b068',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      }
    }
  },
  {
    id: 'vibrant-energy',
    name: 'Vibrant Energy',
    description: 'Dynamic styling with engaging interactions',
    icon: '🎨',
    category: 'balanced',
    restaurantTypes: ['fast-casual', 'trendy', 'bar'],
    selectedComponents: {
      menuItemMovement: 'menu-item-lift',
      categoryHeaderStyle: 'category-gradient-underline',
      menuItemGlow: 'menu-item-strong-glow',
      menuItemTextEffects: ['menu-item-title-color', 'menu-item-description-fade'],
      menuItemImageEffects: [{ id: 'menu-item-image-gradient', subTemplate: 'menu-item-image-gradient-primary' }],
      categoryHeaderTextEffects: ['category-base-styling'],
      buttonHoverEffect: 'button-hover-lift-and-color',
      buttonClickEffect: ['button-click-ripple'],
      buttonPulseEffect: 'button-pulse-glow',
      navBarTextEffects: ['nav-logo-text-glow', 'nav-logo-image-hover-scale'],
      priceTagEnhancements: 'price-tag-single-color',
      errorMessageStyles: ['error-message-enhanced'],
      serviceChoiceStyles: 'service-comprehensive',
      footerEnhancements: ['footer-line']
    },
    globalSettings: {
      ...DEFAULT_GLOBAL_SETTINGS,
      animationSpeed: 'normal',
      // Custom description fade strength
      descriptionFadeStrength: 0.5, // 50% fade strength
      // Custom button shadow hover to use primary color instead of secondary
      buttonShadowHover: {
        type: 'shadow',
        color: 'USE_PRIMARY_COLOR', // Special marker for primary color
        strength: 0.4
      },
      // Enhanced font settings for Dynamic Dish Title Enhancements - both states
      menuItemTextRest: {
        color: '#ffffff',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      },
      menuItemTextHover: {
        color: '#c5b068',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      }
    }
  }
];

// Advanced presets - Feature-rich with multiple enhancements
export const advancedPresets: CodeInjectionPreset[] = [
  {
    id: 'premium-experience',
    name: 'Premium Experience',
    description: 'Comprehensive enhancements for premium brands',
    icon: '👑',
    category: 'advanced',
    restaurantTypes: ['fine-dining', 'luxury', 'premium', 'vietnamese'],
    selectedComponents: {
      menuItemMovement: 'menu-item-lift',
      categoryHeaderStyle: 'category-animated-underline',
      menuItemGlow: 'menu-item-strong-glow',
      menuItemTextEffects: ['menu-item-title-color', 'menu-item-description-fade'],
      menuItemImageEffects: [{ id: 'menu-item-image-gradient', subTemplate: 'menu-item-image-gradient-primary' }],
      categoryHeaderTextEffects: ['category-base-styling'],
      buttonHoverEffect: 'button-hover-lift-and-color',
      buttonClickEffect: ['button-click-ripple'],
      buttonPulseEffect: 'button-pulse-glow',
      navBarTextEffects: ['nav-logo-text-glow', 'nav-logo-image-hover-scale'],
      priceTagEnhancements: 'price-tag-single-color',
      errorMessageStyles: ['error-message-enhanced'],
      socialIconEffects: ['social-icons-enhanced'],
      searchButtonEffects: ['search-button-enhanced'],
      tagBadgeStyles: ['tag-badge-enhanced'],
      serviceChoiceStyles: 'service-premium',
      footerEnhancements: ['footer-line']
    },
    globalSettings: {
      ...DEFAULT_GLOBAL_SETTINGS,
      animationSpeed: 'normal',
      // Custom description fade strength
      descriptionFadeStrength: 0.5, // 50% fade strength
      // Custom button shadow hover to use primary color instead of secondary
      buttonShadowHover: {
        type: 'shadow',
        color: 'USE_PRIMARY_COLOR', // Special marker for primary color
        strength: 0.4
      },
      // Enhanced font settings for Dynamic Dish Title Enhancements - both states
      menuItemTextRest: {
        color: '#ffffff',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      },
      menuItemTextHover: {
        color: '#c5b068',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      }
    }
  },
  {
    id: 'interactive-showcase',
    name: 'Interactive Showcase',
    description: 'Maximum interactivity with all enhancements and welcome messages',
    icon: '🎪',
    category: 'advanced',
    restaurantTypes: ['entertainment', 'theme', 'experience', 'vietnamese', 'colorful'],
    selectedComponents: {
      menuItemMovement: 'menu-item-lift',
      categoryHeaderStyle: 'category-animated-underline',
      menuItemGlow: 'menu-item-strong-glow',
      menuItemTextEffects: ['menu-item-title-color', 'menu-item-description-fade'],
      menuItemImageEffects: [
        { id: 'menu-item-image-gradient', subTemplate: 'menu-item-image-gradient-primary' },
        'menu-item-image-zoom'
      ],
      categoryHeaderTextEffects: ['category-base-styling'],
      buttonHoverEffect: 'button-hover-lift-and-color',
      buttonClickEffect: ['button-click-ripple'],
      buttonPulseEffect: 'button-pulse-dual-brand',
      navBarTextEffects: ['nav-logo-text-glow', 'nav-logo-gradient', 'nav-logo-glow', 'nav-logo-scale', 'nav-logo-image-hover-scale'],
      priceTagEnhancements: 'price-tag-gradient',
      errorMessageStyles: ['error-message-enhanced'],
      socialIconEffects: ['social-icons-enhanced'],
      searchButtonEffects: ['search-button-enhanced'],
      tagBadgeStyles: ['tag-badge-enhanced', 'tag-badge-special'],
      welcomeMessages: 'welcome-greeting-basic',
      serviceChoiceStyles: 'service-premium',
      footerEnhancements: ['footer-line']
    },
    globalSettings: {
      ...DEFAULT_GLOBAL_SETTINGS,
      animationSpeed: 'normal',
      // Custom description fade strength
      descriptionFadeStrength: 0.5, // 50% fade strength
      // Enhanced category header font weight for maximum impact
      fontWeight: 900, // Extra bold category headers
      // Custom button shadow hover to use primary color instead of secondary
      buttonShadowHover: {
        type: 'shadow',
        color: 'USE_PRIMARY_COLOR', // Special marker for primary color
        strength: 0.4
      },
      // Enhanced font settings for Dynamic Dish Title Enhancements - both states
      menuItemTextRest: {
        color: '#ffffff',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      },
      menuItemTextHover: {
        color: '#c5b068',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      }
    }
  },
  {
    id: 'comprehensive-modern',
    name: 'Comprehensive Modern',
    description: 'All modern features with classic styling',
    icon: '🚀',
    category: 'advanced',
    restaurantTypes: ['modern', 'tech-forward', 'innovative', 'indian'],
    selectedComponents: {
      menuItemMovement: 'menu-item-lift',
      categoryHeaderStyle: 'category-animated-underline',
      menuItemGlow: 'menu-item-brand-glow',
      menuItemTextEffects: ['menu-item-title-color', 'menu-item-description-fade'],
      menuItemImageEffects: [
        { id: 'menu-item-image-gradient', subTemplate: 'menu-item-image-gradient-primary' },
        'menu-item-image-zoom'
      ],
      categoryHeaderTextEffects: ['category-base-styling'],
      buttonHoverEffect: 'button-hover-scale',
      buttonClickEffect: ['button-click-ripple'],
      buttonPulseEffect: 'button-pulse-dual-brand',
      navBarTextEffects: ['nav-logo-text-glow', 'nav-logo-glow', 'nav-logo-scale', 'nav-logo-image-hover-scale'],
      priceTagEnhancements: 'price-tag-single-color',
      errorMessageStyles: ['error-message-enhanced'],
      socialIconEffects: ['social-icons-enhanced'],
      searchButtonEffects: ['search-button-enhanced'],
      tagBadgeStyles: ['tag-badge-enhanced'],
      serviceChoiceStyles: 'service-comprehensive',
      footerEnhancements: ['footer-line']
    },
    globalSettings: {
      ...DEFAULT_GLOBAL_SETTINGS,
      animationSpeed: 'normal',
      // Custom description fade strength
      descriptionFadeStrength: 0.5, // 50% fade strength
      // Enhanced category header font weight for maximum impact
      fontWeight: 900, // Extra bold category headers
      // Custom button shadow hover to use primary color instead of secondary
      buttonShadowHover: {
        type: 'shadow',
        color: 'USE_PRIMARY_COLOR', // Special marker for primary color
        strength: 0.4
      },
      // Enhanced font settings for Dynamic Dish Title Enhancements - both states
      menuItemTextRest: {
        color: '#ffffff',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      },
      menuItemTextHover: {
        color: '#c5b068',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      }
    }
  },
  {
    id: 'restaurant-deluxe',
    name: 'Restaurant Deluxe',
    description: 'All modern features with classic styling and welcome messages',
    icon: '🌟',
    category: 'advanced',
    restaurantTypes: ['fine-dining', 'upscale', 'gourmet', 'chef-driven'],
    selectedComponents: {
      menuItemMovement: 'menu-item-lift',
      categoryHeaderStyle: 'category-animated-underline',
      menuItemGlow: 'menu-item-brand-glow',
      menuItemTextEffects: ['menu-item-title-color', 'menu-item-description-fade'],
      menuItemImageEffects: [
        { id: 'menu-item-image-gradient', subTemplate: 'menu-item-image-gradient-primary' },
        'menu-item-image-zoom'
      ],
      categoryHeaderTextEffects: ['category-base-styling'],
      buttonHoverEffect: 'button-hover-scale',
      buttonClickEffect: ['button-click-ripple'],
      buttonPulseEffect: 'button-pulse-dual-brand',
      navBarTextEffects: ['nav-logo-text-glow', 'nav-logo-glow', 'nav-logo-scale', 'nav-logo-image-hover-scale'],
      priceTagEnhancements: 'price-tag-single-color',
      errorMessageStyles: ['error-message-enhanced'],
      socialIconEffects: ['social-icons-enhanced'],
      searchButtonEffects: ['search-button-enhanced'],
      tagBadgeStyles: ['tag-badge-enhanced'],
      welcomeMessages: 'welcome-greeting-basic',
      serviceChoiceStyles: 'service-comprehensive',
      footerEnhancements: ['footer-line']
    },
    globalSettings: {
      ...DEFAULT_GLOBAL_SETTINGS,
      animationSpeed: 'normal',
      // Custom description fade strength
      descriptionFadeStrength: 0.5, // 50% fade strength
      // Enhanced category header font weight for maximum impact
      fontWeight: 900, // Extra bold category headers
      // Custom button shadow hover to use primary color instead of secondary
      buttonShadowHover: {
        type: 'shadow',
        color: 'USE_PRIMARY_COLOR', // Special marker for primary color
        strength: 0.4
      },
      // Enhanced font settings for Dynamic Dish Title Enhancements - both states
      menuItemTextRest: {
        color: '#ffffff',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      },
      menuItemTextHover: {
        color: '#c5b068',
        fontSize: 20, // Increased from 18
        fontWeight: 700, // Increased from 600
        fontStyle: 'normal',
        textTransform: 'none',
        letterSpacing: 0,
        textShadow: 'none'
      }
    }
  }
];

// All presets combined
export const allPresets: CodeInjectionPreset[] = [
  ...minimalPresets,
  ...balancedPresets,
  ...advancedPresets
];

// Presets grouped by category
export const presetsByCategory = {
  minimal: minimalPresets,
  balanced: balancedPresets,
  advanced: advancedPresets
};

// Get preset by ID
export const getPresetById = (id: string): CodeInjectionPreset | undefined => {
  return allPresets.find(preset => preset.id === id);
};

// Get presets by restaurant type
export const getPresetsByRestaurantType = (restaurantType: string): CodeInjectionPreset[] => {
  return allPresets.filter(preset => 
    preset.restaurantTypes?.includes(restaurantType)
  );
};

// Get presets by category
export const getPresetsByCategory = (category: 'minimal' | 'balanced' | 'advanced'): CodeInjectionPreset[] => {
  return presetsByCategory[category];
};

// Default preset for new users
export const defaultPreset: CodeInjectionPreset = minimalPresets[0];